import { type FC, useContext, useEffect, useState } from "react";

import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import { useForm } from "react-hook-form";

import {
  Button,
  CheckboxState,
  Icon,
  Input,
  Text,
  toastError,
  toastSuccess,
} from "@alpacahq/ui/old";

import { UserActions } from "~/v2/api/auth";
import { getAuthRedirectReason } from "~/v2/api/auth/authFailure";
import { getAccessToken } from "~/v2/api/auth/authxToken";
import { initAmplify } from "~/v2/api/auth/config";
import Checkbox from "~/v2/components/Checkbox";
import { withAppBasePath } from "~/v2/utils/appBasePath";
import { eraseCookie } from "~/v2/utils/cookies";
import { AuthContext, AuthState } from "../AuthContext";

export const DEFAULT_PATHS = ["/", "/login"];

// Predates Amplify, which now keeps the Cognito tokens in its own cookies. This
// one is only cleared defensively, so that a token left in a returning user's
// browser by the old dashboard cannot travel with a fresh login.
const LEGACY_AUTH_COOKIE = "dashboard.authtoken";

const MFA_CHALLENGE_TYPES = ["SMS_MFA", "SOFTWARE_TOKEN_MFA"];
const FORM_VALIDATION_SCHEMA = Joi.object({
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .required()
    .messages({
      "string.empty": "Please enter an email",
      "string.email": "Must be a valid email",
    }),
  password: Joi.string().required().messages({
    "string.empty": "Please enter a password",
  }),
});

type AuthCardLoginFields = {
  email: string;
  password: string;
};

export const Login: FC = () => {
  const { setAuthState, setCognitoUser } = useContext(AuthContext);

  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [status, setStatus] = useState<"success" | "error" | "neutral">(
    "neutral",
  );

  const rememberMeEmail = window.localStorage?.getItem("email");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AuthCardLoginFields>({
    resolver: joiResolver(FORM_VALIDATION_SCHEMA),
    defaultValues: { email: rememberMeEmail || "" },
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    // set the email value from local storage if it exists
    if (rememberMeEmail) {
      setRememberMe(true);
    }
  }, [rememberMeEmail]);

  const onSubmit = async (data: AuthCardLoginFields) => {
    // prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    // to avoid possible session issues, clear the stale auth cookie before
    // logging in
    eraseCookie(LEGACY_AUTH_COOKIE);
    setIsSubmitting(true);
    initAmplify();

    // sleep for 500ms to allow the loading state to show
    await new Promise((r) => setTimeout(r, 500));

    try {
      const userOrChallenge = await UserActions.userLogin(
        data.email.toLowerCase(),
        data.password,
      );

      if (
        // if the user is not null and the challenge name is in the list of MFA challenge types
        userOrChallenge.challengeName &&
        MFA_CHALLENGE_TYPES.includes(userOrChallenge.challengeName)
      ) {
        // set the cognito user in the auth context
        setCognitoUser(userOrChallenge);

        // this will prompt the user to enter their MFA code
        setAuthState(AuthState.MFA);
      } else {
        setCognitoUser(userOrChallenge);

        try {
          // Ensure the Cognito session is fully persisted to cookie storage
          // before exchanging the token, so authx can read the current session.
          await UserActions.getCurrentUser();

          // Exchange before marking login successful so failures do not leave
          // the OAuth flow in a partially authenticated state.
          await getAccessToken();
        } catch (err) {
          setCognitoUser(undefined);
          await UserActions.userLogout(
            { global: false },
            getAuthRedirectReason(err),
          );
          throw err;
        }

        // if signIn was successful, oauth knows that the user has logged in
        window.sessionStorage?.setItem("oauth_login", "1");

        // show a success toast
        toastSuccess("Success! Logging you in...");

        // highlight the inputs green
        setStatus("success");

        // sleep for 1.5 seconds to allow the success toast to show
        await new Promise((r) => setTimeout(r, 1500));

        const requestedPathVal =
          window.sessionStorage?.getItem("requested_path") || "";

        // send the user to the requested path if it exists or the home page
        window.location.href = withAppBasePath(
          DEFAULT_PATHS.includes(requestedPathVal) ? "/" : requestedPathVal,
        );
      }
    } catch (err) {
      // remove email if remember me is not checked
      window.localStorage?.removeItem("email");

      // handle any login errors here
      const error = err as Error;
      toastError(error?.message ?? "An error occurred during login");
    } finally {
      if (!rememberMe) {
        window.localStorage?.removeItem("email");
      }

      setIsSubmitting(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-1">
        <Input
          label="Email"
          type="email"
          placeholder="me@example.com"
          status={status}
          errorMsg={errors.email?.message}
          msgPadded
          data-testid="email-input"
          {...register("email")}
        />
        <Input
          label="Password"
          placeholder="••••••••••••••••"
          status={status}
          errorMsg={errors.password?.message}
          type={passwordVisible ? "text" : "password"}
          rightIcon={
            <Icon
              className="cursor-pointer stroke-2"
              name={passwordVisible ? "Eye" : "EyeSlash"}
              onClick={() => setPasswordVisible(!passwordVisible)}
            />
          }
          msgPadded
          data-testid="password-input"
          {...register("password")}
        />
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="rememberMe"
              onChange={() => setRememberMe(!rememberMe)}
              state={
                rememberMe ? CheckboxState.Checked : CheckboxState.Unchecked
              }
            />
            <label
              className="cursor-pointer text-sm text-zinc-900"
              onClick={() => document.getElementById("rememberMe")?.click()}
            >
              Remember me
            </label>
          </div>
          <Text
            onClick={() => setAuthState(AuthState.FORGOT_PASSWORD)}
            className="cursor-pointer text-sm text-zinc-900 underline"
            style={{ fontWeight: 450 }}
            data-testid="forgot-password-link"
          >
            Forgot password?
          </Text>
        </div>
        <input type="submit" className="hidden" />
      </form>
      <div>
        <Button
          variant="v2/filled"
          className="mb-4 w-full"
          loading={isSubmitting}
          disabled={isSubmitting}
          onClick={handleSubmit(onSubmit)}
          data-testid="login-button"
        >
          Continue
        </Button>
        <Text className="text-sm text-zinc-500">
          Don&apos;t have an account yet?{" "}
          <a
            href="/signup"
            className="text-sm text-zinc-900 underline hover:underline"
            style={{ fontWeight: 450 }}
            data-testid="create-account-link"
          >
            Create an account
          </a>
        </Text>
      </div>
    </>
  );
};
